import { ReactElement, useEffect, useMemo } from 'react'
import {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType
} from 'next'
import { readFile } from 'node:fs/promises'
import { join } from 'node:path'

import Breadcrumbs from '@/components/base/breadcrumbs'
import Meta from '@/components/base/meta'
import PageBuilder from '@/components/layout/builder'
import Layout from '@/components/layout/layout'
import Agencies from '@/components/sections/agencies'
import ArticleHero from '@/components/sections/article-hero'
import CollectionChildren from '@/components/sections/collection-children'
import CollectionLanding from '@/components/sections/collection-landing'
import ConceptsPage from '@/components/sections/concepts-page'
import Contacts from '@/components/sections/contacts'
import DownloadPage from '@/components/sections/download-page'
import JournalPage from '@/components/sections/journal-page'
import ProductHero from '@/components/sections/product-hero'
import ProjectHero from '@/components/sections/project-hero'
import ProjectsList from '@/components/sections/projects-list'
import SalesNetwork from '@/components/sections/sales-network'
import StoreHero from '@/components/sections/store-hero'
import StorePage from '@/components/sections/store-page'
import { useStore } from '@/contexts/store/use-store'
import { useHomeHeroStore } from '@/store/home-hero'
import { mapPageStaticProps } from '@/utils/page-data'
import { getPromiseFromFetch } from '@/utils/request'
import { API, MOCK_DATA } from '@/constants'

import { WPPageData, WPRoutes } from '@/types'

// layout del builder che renderizzano gia' un h1
const H1_LAYOUTS = ['hero', 'image_hero', 'text_hero', 'contact', 'product_hero']

export default function Page(
  pageProps: InferGetStaticPropsType<typeof getStaticProps>
) {
  const { strings } = useStore()
  const markIntroPlayed = useHomeHeroStore((state) => state.markIntroPlayed)

  useEffect(() => {
    markIntroPlayed()
  }, [markIntroPlayed])

  const output = useMemo(() => {
    return (
      <>
        <Breadcrumbs pageProps={pageProps} />

        {pageProps.template === 'template/concepts.php' ||
        (pageProps.type === 'page' &&
          !pageProps.template &&
          !pageProps.acf?.builder?.some((section: any) =>
            H1_LAYOUTS.includes(section?.acf_fc_layout)
          )) ? (
          <h1 className="sr-only">{pageProps.title?.rendered}</h1>
        ) : null}

        {pageProps.template === 'template/concepts.php' ? (
          <ConceptsPage pageProps={pageProps} />
        ) : null}

        {pageProps.template === 'template/archive-media.php' ? (
          <DownloadPage pageProps={pageProps} />
        ) : null}

        {pageProps.template === 'template/archive-news.php' ? (
          <JournalPage pageProps={pageProps} />
        ) : null}

        {pageProps.type === 'project' ? (
          <ProjectHero pageProps={pageProps} />
        ) : null}

        {pageProps.type === 'post' ? (
          <ArticleHero pageProps={pageProps} />
        ) : null}

        {pageProps.template === 'template/progetti.php' ? (
          <ProjectsList pageProps={pageProps} />
        ) : null}

        {pageProps.template === 'template/collezione.php' ? (
          <CollectionLanding pageProps={pageProps} />
        ) : null}

        {pageProps.template === 'template/sales-network.php' ? (
          <SalesNetwork pageProps={pageProps} />
        ) : null}

        {pageProps.template === 'template/contacts.php' ? (
          <Contacts
            title={pageProps?.title?.rendered}
            tabs={[
              {
                label: strings?.contacts?.private_form_title,
                form: strings?.contacts?.private_form
              },
              {
                label: strings?.contacts?.professional_form_title,
                form: strings?.contacts?.professional_form
              }
            ]}
          />
        ) : null}

        {pageProps.template === 'template/agencies.php' ? (
          <Agencies pageProps={pageProps} />
        ) : null}

        {pageProps.template === 'template/store.php' ? (
          <StorePage pageProps={pageProps as any} />
        ) : null}

        {pageProps.type === 'taxonomy' &&
        typeof (pageProps as any)?.object === 'object' &&
        (pageProps as any)?.object?.taxonomy === 'product_cat' ? (
          <CollectionChildren
            pageProps={{
              ...pageProps,
              acf: {
                ...pageProps.acf,
                footer: true
              }
            }}
          />
        ) : null}

        {pageProps.type === 'product' ? (
          <ProductHero pageProps={pageProps} />
        ) : null}

        {pageProps.type === 'store' ? (
          <StoreHero pageProps={pageProps} />
        ) : null}

        <PageBuilder pageProps={pageProps} />

        {pageProps.type === 'store' ? (
          <StorePage pageProps={pageProps} />
        ) : null}
      </>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageProps])

  return output
}

Page.getLayout = function getLayout(page: ReactElement, pageProps: WPPageData) {
  return (
    <Layout pageProps={pageProps}>
      <Meta pageProps={pageProps} />
      {page}
    </Layout>
  )
}

export async function getStaticProps({
  params,
  locale,
  defaultLocale
}: GetStaticPropsContext<{ routes: string[] }>) {
  let pageProps: WPPageData | null = null

  if (MOCK_DATA) {
    const routePath = params?.routes?.join('/') || 'home'
    const leafRoute = params?.routes?.at(-1)
    const candidates = [routePath, leafRoute, 'home'].filter(
      (item, index, all) => item && all.indexOf(item) === index
    ) as string[]

    for (const candidate of candidates) {
      try {
        const filePath = join(
          process.cwd(),
          'data',
          'pages',
          `${candidate}.json`
        )
        const data = await readFile(filePath, 'utf-8')
        pageProps = JSON.parse(data)
        break
      } catch {
        // try next candidate
      }
    }

    if (!pageProps) return { notFound: true }
  } else {
    const res = await getPromiseFromFetch<WPPageData>({
      method: 'get',
      url: `/${locale === defaultLocale ? '' : `${locale}/`}${params?.routes?.join('/') || ''}/`
    })

    if (res.status === 404) return { notFound: true }

    pageProps = res.data
  }

  return {
    props: {
      ...(await mapPageStaticProps(pageProps))
    },
    revalidate: 300
  }
}

export async function getStaticPaths({
  defaultLocale,
  locales
}: GetStaticPathsContext) {
  const routes: WPRoutes = MOCK_DATA
    ? require('@/data/routes.json')
    : (
        await getPromiseFromFetch<WPRoutes>({
          method: 'get',
          url: API.ROUTES
        })
      ).data

  return {
    paths: routes
      .filter((route) => {
        if (!route?.locale) return true
        if (!locales || locales.length === 0) return true

        return locales.includes(route.locale)
      })
      .map((route) => {
        const path = {
          params: {
            routes: route.routes.filter((item) => item !== route.locale)
          }
        } as { params: { routes: string[] }; locale?: string }

        if (route.locale && route.locale !== defaultLocale) {
          path.locale = route.locale
        }

        return path
      }),
    fallback: 'blocking' // false
  }
}
